/**
 * The photo gallery on a service or event page: the main photo, the extra
 * photos as thumbnails by count, and a full-screen viewer that steps through
 * all of them. Draws nothing when the offering has no photo.
 */
import type { SitePhoto } from '@telify/shared';

import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'photo-gallery',
  description:
    "An offering's main photo with up to three thumbnails under it (the third carries \"+N more\" past three), each opening a full-screen viewer.",
  options: {},
  parts: {
    gallery: { description: 'The whole gallery.', required: false },
    main: { description: 'The main photo; opens the viewer on it.', required: false, role: 'photo' },
    thumbs: { description: 'The row of thumbnails under the main photo.', required: false },
    thumb: { description: 'One thumbnail; opens the viewer on its photo.', required: false },
    more: { description: 'The "+N more" label over the last thumbnail when more photos are hidden.', required: false },
    viewer: { description: 'The full-screen viewer.', required: false },
    'viewer-backdrop': { description: "The viewer's dimmed backdrop; a click closes the viewer.", required: false },
    'viewer-photo': { description: 'The photo shown whole in the viewer.', required: false },
    'viewer-prev': { description: 'The previous-photo button; hidden on the first photo.', required: false },
    'viewer-next': { description: 'The next-photo button; hidden on the last photo.', required: false },
    'viewer-close': { description: 'The close button.', required: false },
    'viewer-count': { description: 'Which photo is showing, such as "3 of 8".', required: false },
  },
  actions: {
    open: { description: 'Opens the viewer on one photo.', args: [{ name: 'index', kind: 'value', type: 'number', min: 0 }] },
    go: { description: 'Shows the photo before or after the current one.', args: [{ name: 'step', kind: 'value', type: 'number', min: -1, max: 1 }] },
    close: { description: 'Closes the viewer.', args: [] },
  },
  events: {
    opened: 'A visitor opened the viewer.',
    closed: 'A visitor closed the viewer.',
  },
});

/** One thumbnail the gallery draws under the main photo. */
export interface PhotoGalleryThumb {
  /** The photo. */
  readonly photo: SitePhoto;
  /** Its place in `photos`, which the viewer opens on. */
  readonly index: number;
  /** How many more photos this thumbnail stands for, drawn as "+N more"; 0 for none. */
  readonly more: number;
}

/** What the photo gallery knows. */
export interface PhotoGalleryState {
  /** loading until the offering's first read lands; error when it failed. */
  readonly status: 'loading' | 'ready' | 'error';
  /** Every photo in order: the main photo first, then the extra photos in the studio's order. Empty when none. */
  readonly photos: readonly SitePhoto[];
  /** The thumbnails under the main photo: up to three extra photos, the third carrying the hidden count. */
  readonly thumbs: readonly PhotoGalleryThumb[];
  /** The photo the viewer shows, an index into `photos`, or null while the viewer is closed. */
  readonly viewerIndex: number | null;
  /** True when the viewer shows a photo before the current one. */
  readonly hasPrev: boolean;
  /** True when the viewer shows a photo after the current one. */
  readonly hasNext: boolean;
}

/** What a drawing can ask the photo gallery to do. */
export interface PhotoGalleryActions {
  /** Open the viewer on the photo at this index; an index outside `photos` is ignored. */
  readonly open: (index: number) => void;
  /** Show the photo `step` places away (-1 previous, 1 next); stops at either end. */
  readonly go: (step: number) => void;
  /** Close the viewer. */
  readonly close: () => void;
}

/** What the photo gallery tells its host. */
export interface PhotoGalleryEvents {
  /** The viewer opened on the photo at this index. */
  readonly opened: { readonly index: number };
  /** The viewer closed. */
  readonly closed: Record<string, never>;
}
